/**
 * Shared utilities for extracting and sanitizing email body HTML
 * (strip base tags, rewrite cid: URLs) before rendering in iframe.
 */

/** Placeholder for inline email images (cid:) – no network request */
const INLINE_IMAGE_PLACEHOLDER = '<span class="email-inline-image-placeholder" title="Immagine inline (non disponibile)">[Immagine]</span>';

/**
 * Removes <base> tags from HTML so relative URLs and data URLs are not resolved incorrectly.
 */
export function stripBaseTags(html: string): string {
    if (!html || !html.includes("<base")) return html;
    return html.replace(/<base\s[^>]*>/gi, "").replace(/<\/base\s*>/gi, "");
}

/**
 * Replaces inline email images (cid:) with a placeholder element so the browser never
 * requests cid: or data: URLs (avoids ERR_UNKNOWN_URL_SCHEME and ERR_INVALID_URL).
 */
export function rewriteCidUrlsInHtml(html: string): string {
    if (!html || !html.includes("cid:")) return html;
    return html
        .replace(/<img\s[\s\S]*?\/?>/gi, (match) => (/src\s*=\s*["']cid:/i.test(match) ? INLINE_IMAGE_PLACEHOLDER : match))
        .replace(/\burl\s*\(\s*["']?cid:[^"')]*["']?\s*\)/gi, "none");
}

/**
 * Extracts body content from full HTML document and injects head styles.
 * Returns HTML suitable for rendering inside an iframe (no bleed-through to parent).
 */
export function extractBodyContent(html: string): string {
    if (!html) return "";
    const normalizedHtml = html.trim();
    if (typeof DOMParser !== "undefined") {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(normalizedHtml, "text/html");
            const body = doc.body;
            const head = doc.head;
            const bodyInnerHTML = body?.innerHTML ?? null;
            const trimmed = bodyInnerHTML?.trim();
            if (body && trimmed && trimmed.length > 0) {
                const styleContents = Array.from(head.querySelectorAll("style"))
                    .map((style) => style.textContent || style.innerHTML)
                    .join("\n");
                const linkStyles = Array.from(head.querySelectorAll('link[rel="stylesheet"]'))
                    .map((link) => link.outerHTML)
                    .join("\n");
                const result = styleContents || linkStyles ? `${styleContents ? `<style>${styleContents}</style>` : ""}${linkStyles}${trimmed}` : trimmed;
                return stripBaseTags(rewriteCidUrlsInHtml(result));
            }
        } catch {
            // fallback to regex
        }
    }
    const bodyMatch = normalizedHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const raw = bodyMatch?.[1]?.trim() ?? (normalizedHtml.toLowerCase().startsWith("<!doctype") || normalizedHtml.includes("<html") ? "" : normalizedHtml);
    return raw ? stripBaseTags(rewriteCidUrlsInHtml(raw)) : raw;
}
